// End-of-window Holdings card. Two parts:
//
//   1. Top strip — three KPI tiles (Total Portfolio / Cash on Hold /
//      Equity Value) sitting next to a small donut that visualises
//      the cash vs equity split.
//
//   2. Bottom table — one row per stock still held on the end-date,
//      with avg buy price, current price, absolute profit, profit %.
//
// Theme parity is the brief: this should look indistinguishable from
// the Trades-table card already on the dashboard. Same `.card` shell,
// same header layout, same Th sort + Hint pattern, same `.mono`
// numerics, same up/down colour tokens.

function HoldingsCard({ summary }) {
    const positions = summary.positions || [];
    const totalValue = summary.totalValue || 0;
    const equityValue = summary.equityValue || 0;
    const cashOnHold = (summary.portfolio && summary.portfolio.cashBalance != null
        ? Number(summary.portfolio.cashBalance.amount ?? summary.portfolio.cashBalance)
        : Math.max(0, totalValue - equityValue));
    // Realised + unrealised P&L surfaced on the API top level
    // (BacktestSummary.unrealizedPnL / realizedPnL). Default to 0
    // for backward-compat with older recorded responses.
    const unrealisedPnL = Number(summary.unrealizedPnL || 0);
    const realisedPnL = Number(summary.realizedPnL || 0);

    const [sortBy, setSortBy] = React.useState({ key: 'value', dir: 'desc' });

    // Normalise once. Each row carries the bits the table needs;
    // derived fields (value, profit, profit%) are pre-computed here so
    // the sort comparator stays trivial.
    const rows = React.useMemo(() => positions.map((p, i) => {
        const qty = Number(p.quantity);
        const avg = Number(p.averagePrice);
        const cur = Number(p.currentPrice);
        const value = qty * cur;
        return {
            id: `${p.symbol}-${i}`,
            symbol: p.symbol,
            companyName: p.companyName,
            upstoxUrl: p.upstoxUrl,
            qty,
            avg,
            cur,
            value,
            profit: typeof p.profit === 'number' ? p.profit : (cur - avg) * qty,
            profitPct: typeof p.profitPercentage === 'number'
                ? p.profitPercentage
                : (avg > 0 ? (cur / avg - 1) * 100 : 0),
        };
    }), [positions]);

    const sorted = React.useMemo(() => {
        const copy = rows.slice();
        const dir = sortBy.dir === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            const k = sortBy.key;
            if (k === 'stock') return a.symbol.localeCompare(b.symbol) * dir;
            if (k === 'profitPct') return (a.profitPct - b.profitPct) * dir;
            // numeric default (value, profit, qty, avg, cur)
            return ((a[k] ?? 0) - (b[k] ?? 0)) * dir;
        });
        return copy;
    }, [rows, sortBy]);

    const toggleSort = (key) => {
        setSortBy((prev) => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: 'desc' });
    };

    const Th = ({ col, label, align = 'left', hint }) => (
        <th style={{ textAlign: align, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort(col)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {label}
                {sortBy.key === col && (
                    <span style={{ color: 'var(--text-2)', fontSize: 9 }}>
                        {sortBy.dir === 'asc' ? '▲' : '▼'}
                    </span>
                )}
                {hint && (
                    <span onClick={(e) => e.stopPropagation()}>
                        <UI.Hint width={300}>{hint}</UI.Hint>
                    </span>
                )}
            </span>
        </th>
    );

    return (
        <div className="card mt-5" style={{ padding: 0, overflow: 'hidden' }}>
            {/* ── Top strip: 5-line summary + donut ──────────────────────
                Compact label/value rows (no big tiles) so the strip
                stays at one screen-line of vertical space and shows
                all five portfolio numbers at once. */}
            <div className="holdings-summary">
                <div className="lines">
                    <div className="line">
                        <span className="lbl">Total portfolio</span>
                        <span className="val">{UI.fmtINR(totalValue, { compact: true })}</span>
                    </div>
                    <div className="line">
                        <span className="lbl">Cash on hold</span>
                        <span className="val">{UI.fmtINR(cashOnHold, { compact: true })}</span>
                    </div>
                    <div className="line">
                        <span className="lbl">Equity value</span>
                        <span className="val">{UI.fmtINR(equityValue, { compact: true })}</span>
                    </div>
                    <div className={`line ${unrealisedPnL > 0 ? 'up' : unrealisedPnL < 0 ? 'down' : ''}`}>
                        <span className="lbl">Unrealised P&L</span>
                        <span className="val">{`${unrealisedPnL >= 0 ? '+' : ''}${UI.fmtINR(unrealisedPnL)}`}</span>
                    </div>
                    <div className={`line ${realisedPnL > 0 ? 'up' : realisedPnL < 0 ? 'down' : ''}`}>
                        <span className="lbl">Realised P&L</span>
                        <span className="val">{`${realisedPnL >= 0 ? '+' : ''}${UI.fmtINR(realisedPnL)}`}</span>
                    </div>
                </div>
                <div className="donut-cell">
                    <PerStockDonut rows={rows} />
                </div>
            </div>

            {/* ── Holdings table ───────────────────────────────────────── */}
            <div className="row between wrap gap-3" style={{ padding: '18px 20px 14px' }}>
                <div>
                    <div className="fw-600" style={{ fontSize: 'var(--text-lg)', letterSpacing: 'var(--tracking-heading)' }}>Holdings on end-date</div>
                    <div className="t-sm tx-3 mt-1 tnum">
                        {rows.length} {rows.length === 1 ? 'position' : 'positions'} marked-to-market
                    </div>
                </div>
                {/* Mobile-only sort dropdown — table headers are
                    hidden at <=768px so column-click sorting isn't
                    reachable. Drives the same sortBy state. */}
                {rows.length > 0 && (
                    <select className="sort-mobile select"
                        style={{ width: 200 }}
                        value={`${sortBy.key}-${sortBy.dir}`}
                        onChange={(e) => {
                            const [key, dir] = e.target.value.split('-');
                            setSortBy({ key, dir });
                        }}>
                        <option value="value-desc">Sort: Market value (high→low)</option>
                        <option value="value-asc">Sort: Market value (low→high)</option>
                        <option value="profit-desc">Sort: P&L (high→low)</option>
                        <option value="profit-asc">Sort: P&L (low→high)</option>
                        <option value="profitPct-desc">Sort: P&L % (high→low)</option>
                        <option value="profitPct-asc">Sort: P&L % (low→high)</option>
                        <option value="stock-asc">Sort: Stock (A→Z)</option>
                        <option value="stock-desc">Sort: Stock (Z→A)</option>
                    </select>
                )}
            </div>

            {rows.length === 0 ? (
                // Designed empty state — replaces the apologetic single
                // line with a friendly icon + headline + explanation
                // that frames "every position closed" as the *result*
                // (not a missing render).
                <div style={{
                    borderTop: '1px solid var(--border)',
                    padding: 'var(--space-9) var(--space-5)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    gap: 'var(--space-3)',
                }}>
                    <div className="row center" style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        color: 'var(--text-3)',
                    }}>
                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="6" width="18" height="14" rx="2" />
                            <path d="M3 10h18" />
                            <path d="M8 14h2" />
                        </svg>
                    </div>
                    <div className="fw-600 tx" style={{ fontSize: 'var(--text-md)' }}>All positions closed</div>
                    <div className="t-sm tx-3" style={{
                        maxWidth: 380, textAlign: 'center', lineHeight: 1.5,
                    }}>
                        The strategy exited every position before the window ended. The
                        realised P&amp;L above is the final result — there are no open
                        positions to mark to market.
                    </div>
                </div>
            ) : (
                <div style={{ maxHeight: 460, overflow: 'auto', borderTop: '1px solid var(--border)' }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <Th col="stock" label="Stock" />
                                <Th col="qty" label="Qty" align="right" />
                                <Th col="avg" label="Avg buy" align="right" />
                                <Th col="cur" label="Current" align="right" />
                                <Th col="value" label="Market value" align="right" />
                                <Th col="profit" label="P&amp;L" align="right"
                                    hint={<>
                                        <strong>Unrealised P&amp;L</strong> — open-position profit if you sold
                                        every lot at the end-date close: <code>(current − avg buy) × qty</code>.
                                        Excludes taxes/brokerage.
                                    </>} />
                                <Th col="profitPct" label="P&amp;L %" align="right"
                                    hint={<>
                                        <strong>Profit %</strong> — relative gain on cost basis:{' '}
                                        <code>(current / avg buy − 1) × 100</code>. Treats the position like a
                                        single buy at the average cost — doesn't care when individual lots were
                                        purchased.
                                    </>} />
                            </tr>
                        </thead>
                        <tbody className="stagger">
                            {sorted.map((r) => (
                                <tr key={r.id} className="row-fade-in">
                                    <td className="td-stock">
                                        <div className="row gap-3">
                                            <UI.CompanyLogo symbol={r.symbol} size={28} />
                                            <div className="min-w-0">
                                                {r.upstoxUrl ? (
                                                    <a href={r.upstoxUrl} target="_blank" rel="noopener noreferrer"
                                                        className="t-base fw-600 tx trk-base"
                                                        translate="no"
                                                        style={{
                                                            textDecoration: 'none',
                                                            borderBottom: '1px dashed var(--text-4)',
                                                        }}
                                                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--accent-text)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text)'}>
                                                        {r.companyName || r.symbol}
                                                    </a>
                                                ) : (
                                                    <div className="t-base fw-600 tx trk-base" translate="no">
                                                        {r.companyName || r.symbol}
                                                    </div>
                                                )}
                                                <div className="mono t-xs tx-3 mt-1" translate="no">
                                                    {r.symbol}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td data-label="Qty" align="right" className="mono tnum t-base tx-2">
                                        {r.qty.toLocaleString('en-IN')}
                                    </td>
                                    <td data-label="Avg buy" align="right" className="mono tnum t-base tx-2">
                                        ₹{r.avg.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td data-label="Current" align="right" className="mono tnum t-base tx-2">
                                        ₹{r.cur.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </td>
                                    <td data-label="Market value" align="right" className="mono tnum t-base fw-600">
                                        {UI.fmtINR(r.value)}
                                    </td>
                                    <td data-label="P&L" align="right" className="mono tnum t-base fw-600" style={{
                                        color: r.profit >= 0 ? 'var(--up)' : 'var(--down)',
                                    }}>
                                        {r.profit >= 0 ? '+' : ''}{UI.fmtINR(r.profit)}
                                    </td>
                                    <td data-label="P&L %" align="right" className="mono tnum t-base fw-600" style={{
                                        color: r.profitPct >= 0 ? 'var(--up)' : 'var(--down)',
                                    }}>
                                        {UI.fmtPct(r.profitPct)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Per-stock allocation donut. One slice per held position sized by
// marketValue. Centre shows TOTAL ₹ value. Hover a slice → tooltip
// with the stock name, ₹ value, and percent. Click a slice with
// upstoxUrl → opens the Upstox page in a new tab (parity with the
// row's hyperlinked stock name in the table below).
//
// Replaces the older SplitDonut (equity vs cash) per the user
// review of Phase 3.7: per-stock allocation is more useful than the
// equity-vs-cash split, which is essentially deployed-vs-idle and
// already legible from the equity curve above.
//
// Slot colors recycle through chart-1..4 keyed off the row's
// original index — same logic as the mobile donut, so the slice
// color matches the colored bar in the table row regardless of how
// the table is sorted.
function PerStockDonut({ rows }) {
    const palette = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];
    const visible = (rows || []).filter((r) => r.value > 0);
    const total = visible.reduce((acc, r) => acc + r.value, 0);

    const size = 140;
    const stroke = 18;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;

    const [hover, setHover] = React.useState(null);
    const [pinned, setPinned] = React.useState(null); // tap-locked tooltip on touch

    if (visible.length === 0 || total <= 0) {
        return (
            <div className="row center" style={{ width: size, height: size }}>
                <div className="t-sm tx-3">No open positions</div>
            </div>
        );
    }

    // Pre-compute slice spans (start angle + sweep) so hit-testing
    // and rendering use the same source of truth.
    let cumul = 0;
    const slices = visible.map((row, i) => {
        const frac = row.value / total;
        const startFrac = cumul;
        cumul += frac;
        return {
            row,
            i,
            color: palette[i % palette.length],
            frac,
            startFrac,
            len: c * frac,
        };
    });

    const active = pinned ?? hover;

    return (
        <div className="row center gap-3" style={{ paddingLeft: 'var(--space-2)', position: 'relative' }}>
            <div className="flex-none" style={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                    role="img"
                    aria-label={(() => {
                        if (visible.length === 0 || total <= 0) {
                            return 'Allocation donut, no open positions.';
                        }
                        const sorted = visible.slice().sort((a, b) => b.value - a.value);
                        const top = sorted.slice(0, 3);
                        const restCount = sorted.length - top.length;
                        const segs = top.map((row) => `${row.companyName || row.symbol} ${((row.value / total) * 100).toFixed(0)}%`).join(', ');
                        const tail = restCount > 0 ? `, and ${restCount} others` : '';
                        return `Allocation donut, ${UI.fmtINR(total, { compact: true })} total. ${segs}${tail}.`;
                    })()}
                    onMouseLeave={() => setHover(null)}>
                    {/* Background track — only visible if rounding leaves
                        a sub-pixel gap between slices. */}
                    <circle cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke="var(--surface-2)" strokeWidth={stroke} />
                    {slices.map((s) => {
                        const isActive = active != null && active.symbol === s.row.symbol;
                        return (
                            <circle key={s.row.id}
                                cx={size / 2} cy={size / 2} r={r}
                                fill="none"
                                stroke={s.color}
                                strokeWidth={isActive ? stroke + 2 : stroke}
                                strokeDasharray={`${s.len} ${c - s.len}`}
                                strokeDashoffset={c / 4 - c * s.startFrac}
                                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                                strokeLinecap="butt"
                                style={{
                                    cursor: s.row.upstoxUrl ? 'pointer' : 'default',
                                    transition: 'stroke-width var(--motion-fast) var(--ease-out)',
                                    opacity: active && active.symbol !== s.row.symbol ? 0.6 : 1,
                                }}
                                onMouseEnter={() => setHover({ symbol: s.row.symbol, row: s.row, frac: s.frac })}
                                onClick={() => {
                                    if (s.row.upstoxUrl) {
                                        window.open(s.row.upstoxUrl, '_blank', 'noopener,noreferrer');
                                    } else {
                                        // No URL — toggle a pinned tooltip so a
                                        // mouseless or mobile user can still
                                        // read the values.
                                        setPinned((p) => p && p.symbol === s.row.symbol
                                            ? null
                                            : { symbol: s.row.symbol, row: s.row, frac: s.frac });
                                    }
                                }} />
                        );
                    })}
                </svg>
                <div className="col center" style={{
                    position: 'absolute', inset: 0,
                    alignItems: 'center',
                    pointerEvents: 'none',
                }}>
                    <div className="eyebrow">Total</div>
                    <div className="mono tnum fw-600" style={{ fontSize: 'var(--text-lg)', lineHeight: 1.1 }}>
                        {UI.fmtINR(total, { compact: true })}
                    </div>
                </div>
            </div>

            {/* Hover/pin tooltip card. Lives outside the SVG so the
                long company-name text isn't clipped by the donut's
                viewBox. */}
            {active && (
                <div className="card" style={{
                    position: 'absolute',
                    top: -8, left: size + 16,
                    padding: 'var(--space-3) var(--space-4)',
                    minWidth: 180,
                    boxShadow: 'var(--shadow-md)',
                    pointerEvents: 'none',
                    zIndex: 1,
                }}>
                    <div className="row gap-2" style={{ alignItems: 'center' }}>
                        <span className="flex-none" style={{
                            width: 10, height: 10, borderRadius: 'var(--radius-xs)',
                            background: palette[(slices.findIndex((s) => s.row.symbol === active.symbol)) % palette.length],
                        }} />
                        <span className="fw-600 tx" style={{ fontSize: 'var(--text-base)' }}>
                            {active.row.companyName || active.row.symbol}
                        </span>
                    </div>
                    <div className="row between t-sm tx-2 mt-2" style={{ gap: 'var(--space-3)' }}>
                        <span>Market value</span>
                        <span className="mono tnum fw-600 tx">{UI.fmtINR(active.row.value, { compact: true })}</span>
                    </div>
                    <div className="row between t-sm tx-2 mt-1" style={{ gap: 'var(--space-3)' }}>
                        <span>Allocation</span>
                        <span className="mono tnum fw-600 tx">{(active.frac * 100).toFixed(1)}%</span>
                    </div>
                </div>
            )}
        </div>
    );
}

window.HoldingsCard = HoldingsCard;
